/* eslint-disable max-lines */
import { debounce } from 'underscore'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'

import RthPlot from 'pages/shared/Device/RthPlot'
import Legend from 'pages/Device/ThermostatRthPanel/Legend'
import legendDataParser from 'pages/Device/ThermostatRthPanel/Chart/legendDataparser'
import { getDefaultSelectedSeriesItems } from 'pages/Device/ThermostatRthPanel/Chart/plotHelper'
import { GetLegendTooltipData } from 'pages/Device/ThermostatRthPanel/Legend/tooltipDataBuilder'

import GA4 from 'helpers/ga4'
import { chartHeight } from 'helpers/plotly/utils'

import { color } from 'styles/colors'

const propTypes = {
  deviceId: PropTypes.string.isRequired,
  startTime: PropTypes.string.isRequired,
  endTime: PropTypes.string.isRequired,
  timeZone: PropTypes.string.isRequired,
  isZoomed: PropTypes.bool.isRequired,
  isControlHub: PropTypes.bool.isRequired,
  zoomedCallback: PropTypes.func.isRequired,
  freezeMouseHover: PropTypes.bool.isRequired,
  freezeMouseHoverCallback: PropTypes.func.isRequired,
  chartDivId: PropTypes.string.isRequired,
  updateSelectedLegendsCallback: PropTypes.func.isRequired,
  showFullScreen: PropTypes.bool.isRequired,
  rthData: PropTypes.shape()
}

const defaultProps = {
  rthData: null
}

const DOUBLE_CLICK_LATENCY = 500

const Plot = ({ deviceId, startTime, endTime, timeZone, isControlHub, isZoomed, rthData, zoomedCallback, freezeMouseHover, freezeMouseHoverCallback, chartDivId, updateSelectedLegendsCallback }) => {
  const doubleClickEventAtRef = useRef(null)
  const containerRef = useRef(null)

  const defaultSelectedLegendItems = useMemo(() => getDefaultSelectedSeriesItems(deviceId, rthData), [deviceId, rthData])
  const [selectedLegends, setSelectedLegends] = useState({ ...defaultSelectedLegendItems })
  const [isChartHovered, setIsChartHovered] = useState(false)
  const [mouseCursorPosition, setMouseCursorPosition] = useState(null)
  const [timeAtCursor, setTimeAtCursor] = useState(null)
  const [plotlyRevision, setPlotlyRevision] = useState(1) // needed to handle multiple zoom in events
  const [graphElement, setGraphElement] = useState(undefined)
  const [parseLegendData, setParseLegendData] = useState(null)
  const [alertsWithInRange, setAlertsWithInRange] = useState(null)

  const onRelayout = useCallback(({ autosize }) => {
    if (doubleClickEventAtRef.current) {
      return
    }

    if (!autosize) {
      zoomedCallback(true)
      setPlotlyRevision(plotlyRevision + 1)
    }
  }, [plotlyRevision, zoomedCallback])

  const onUnhover = useCallback((event) => {
    event.event.preventDefault()
    if (doubleClickEventAtRef.current || event.event.type !== 'mouseout') { return }

    setTimeAtCursor(null)
    setIsChartHovered(false)
  }, [])

  const onHover = useCallback((time, offset) => {
    if (doubleClickEventAtRef.current) { return }

    setIsChartHovered(true)
    setTimeAtCursor(time) // As plotly does not support timezone we are manually converting
    setMouseCursorPosition(offset) // offsetX is the mouse hover position in the graph updating when mouse move
  }, [])

  const onDoubleClick = useCallback(() => {
    doubleClickEventAtRef.current = Date.now()
    freezeMouseHoverCallback(true)
    GA4.sendEvent({ category: 'Customer Detail Page', action: 'RTH Hover Frozen' })
  }, [freezeMouseHoverCallback])

  const onClick = useCallback((timeAtCursor, offset) => {
    const time = Date.now()

    if ((time - doubleClickEventAtRef.current) > DOUBLE_CLICK_LATENCY) {
      if (doubleClickEventAtRef.current) {
        GA4.sendEvent({ category: 'Customer Detail Page', action: 'RTH Hover Unfrozen' })
      }

      freezeMouseHoverCallback(false)
      doubleClickEventAtRef.current = null

      if (timeAtCursor != null && offset != null) {
        onHover(timeAtCursor, offset)
      }
    }
  }, [freezeMouseHoverCallback, onHover])

  useEffect(() => {
    setIsChartHovered(false)
    setTimeAtCursor(null)
    setMouseCursorPosition(null)
    freezeMouseHoverCallback(false)
    doubleClickEventAtRef.current = null
  }, [plotlyRevision, isZoomed, freezeMouseHoverCallback])

  useEffect(() => {
    if (!isZoomed) {
      zoomedCallback(false)
    }
  }, [isZoomed, zoomedCallback])

  useEffect(() => {
    const observer = new ResizeObserver(debounce(() => setPlotlyRevision(Date.now().valueOf()), 500))
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect() // unsubscribe the event on UI element removed from dom
  }, [])

  useEffect(() => {
    updateSelectedLegendsCallback(selectedLegends)
  }, [updateSelectedLegendsCallback, selectedLegends])

  const onLegendItemSelectAllClick = (series, selectAll) => {
    const updatedSelectedLegends = selectAll ? _.union(selectedLegends.series, series) : _.without(selectedLegends.series, ...series)
    setSelectedLegends({ ...selectedLegends, series: updatedSelectedLegends })
  }

  const onIndoorLegendItemSelectAllClick = (seriesNames, selectAll) => {
    const updatedZoneSeries = _.map(rthData.zones, (zone) => {
      return { name: zone.name, series: selectAll ? seriesNames : [] }
    })
    setSelectedLegends({ ...selectedLegends, zoneSeries: updatedZoneSeries })
  }

  const onLegendItemClick = (item) => {
    const updatedSelectedLegends = selectedLegends.series.includes(item) ? _.without(selectedLegends.series, item) : _.union(selectedLegends.series, [item])
    setSelectedLegends({ ...selectedLegends, series: updatedSelectedLegends })
  }

  const onIndoorLegendItemClick = (seriesName, zoneName) => {
    const zone = _.find(selectedLegends.zoneSeries, zone => zone.name === zoneName) || { name: zoneName, series: [] }

    zone.series = zone.series.includes(seriesName) ? _.without(zone.series, seriesName) : _.union(zone.series, [seriesName])
    const updatedZoneSeries = [..._.without(selectedLegends.zoneSeries, zone), zone]
    setSelectedLegends({ ...selectedLegends, zoneSeries: updatedZoneSeries })
  }

  const onIndoorZoneClick = (series, zoneName) => {
    const updatedZoneSeries = _.map(selectedLegends.zoneSeries, zone => {
      if (zone.name === zoneName) {
        return { name: zone.name, series: _.isEmpty(zone.series) ? series : [] }
      } else {
        return { name: zone.name, series: zone.series }
      }
    })

    setSelectedLegends({ ...selectedLegends, zoneSeries: updatedZoneSeries })
  }

  const onClickAlertsLegendItem = (item) => {
    const updatedSelectedAlertSeverities = selectedLegends.alerts.includes(item) ? _.without(selectedLegends.alerts, item) : _.union(selectedLegends.alerts, [item])
    setSelectedLegends({ ...selectedLegends, alerts: updatedSelectedAlertSeverities })
  }

  const onClickAlertsLegendAllOrNone = (_, selectAllClicked) => {
    const updatedSelectedAlertSeverities = selectAllClicked ? ['critical', 'major', 'minor', 'major-sda', 'minor-sda'] : []
    setSelectedLegends({ ...selectedLegends, alerts: updatedSelectedAlertSeverities })
  }

  const onLegendDataUpdated = useCallback((legendData, alerts) => {
    setParseLegendData(legendData)
    setAlertsWithInRange(alerts)
  }, [])

  return (
    <>
      <S.ChartContainer ref={containerRef}>
        <RthPlot
          {...{
            deviceId,
            chartDivId,
            startTime,
            endTime,
            timeZone,
            isZoomed,
            rthData,
            selectedLegends,
            plotlyRevision,
            freezeMouseHover
          }}
          onClickCallback={onClick}
          onHoverCallback={onHover}
          onUnhoverCallback={onUnhover}
          onRelayoutCallback={onRelayout}
          onDoubleClickCallback={onDoubleClick}
          updateGraphElementCallback={(element) => setGraphElement(element)}
          onLegendDataCallback={onLegendDataUpdated}
        />

        {isChartHovered && (
          <S.ToolTipCrossHair left={mouseCursorPosition} width={2} height={chartHeight(graphElement) + 10}>
            <S.ArrowDropUpIcon top={chartHeight(graphElement) - 25} />
          </S.ToolTipCrossHair>
        )}
      </S.ChartContainer>
      {parseLegendData &&
        <Legend
          timeAtCursor={timeAtCursor}
          selectedLegends={selectedLegends}
          isChartHovered={isChartHovered}
          isControlHub={isControlHub}
          alertsInVisibleRange={alertsWithInRange}
          onLegendItemClickCallback={onLegendItemClick}
          onIndoorLegendItemClickCallback={onIndoorLegendItemClick}
          onLegendItemSelectAllClickCallback={onLegendItemSelectAllClick}
          onIndoorLegendItemSelectAllClickCallback={onIndoorLegendItemSelectAllClick}
          onIndoorZoneClickCallback={onIndoorZoneClick}
          onClickAlertsLegendItem={onClickAlertsLegendItem}
          onClickAlertsLegendAllOrNone={onClickAlertsLegendAllOrNone}
          isLegendItemHasData={legendDataParser.isLegendItemHasData}
          timeZone={timeZone}
          legendData={parseLegendData}
          legendDataForTimeAtCursor={timeAtCursor ? GetLegendTooltipData(timeAtCursor / 1000, rthData) : null}
        />
      }
    </>
  )
}

Plot.propTypes = propTypes
Plot.defaultProps = defaultProps

export default memo(Plot)

const S = {
  ChartContainer: styled.div`
    text-align: center;
    position: relative;
    margin: 0 auto 40px auto;
    width: 95%;
    pointer-events: none;
  `,
  ArrowDropUpIcon: styled(ArrowDropUpIcon)`
    font-size: 75px;
    margin-left: -38px;
    position: absolute;
    pointer-events: none;
    top:  ${({ top }) => (`${top}px`)};
  `,
  ToolTipCrossHair: styled.div.withConfig({
    shouldForwardProp: (prop) => !['left', 'width', 'height'].includes(prop)
  })`
    background-color: ${color('rthLegendBorderHover')};
    height: ${({ height }) => (`${height}px`)};
    width: ${({ width }) => (`${width}px`)};
    pointer-events: none;
    position: absolute;
    left: ${({ left, width }) => (`${left - (width * 0.5)}px`)};
    top: 0;
    z-index: 1;
  `
}
